import { Button, Spacer } from "@hackthenorth/north";
import React from "react";

import { ReturningSponsorInviteStatus } from "src/api/types.generated";
import { useUserContext } from "src/shared/contexts";
import { getSponsorAuthUrl } from "src/shared/utils/sponsorAuth";
import { EmptyFallingStackSVG, LaptopGearSVG } from "src/static/img";
import AwSnap, { TAwSnapImage } from "src/views/error/AwSnap";

import { useGetReturningSponsorInviteCodeQuery } from "./graphql/GetReturningSponsorInviteCode.generated";

const DEFAULT_MESSAGE =
  "We couldn’t find attendee access for your account for Hack the North 2026. Please sign in with the email address on your invitation. If you still need help, contact hello@hackthenorth.com.";

const RETURNING_SPONSOR_MESSAGE = (companyName?: string | null) => {
  const company = companyName || "your company";
  return (
    `We're happy ${company} is sponsoring HTN this year! \n` +
    `Click below to sign into ${company}'s dashboard`
  );
};

const MISSING_INVITE_MESSAGE =
  "We're excited that your company is sponsoring Hack the North this year! \n" +
  "Please use the invite link sent to you by your sponsorship coordinator to access this year's sponsor dashboard. \n" +
  "If you have any questions, please reach out to your Hack the North contact.";

const NOT_SPONSORING_MESSAGE = (companyName?: string | null) =>
  `It looks like ${
    companyName || "your company"
  } is not sponsoring Hack the North this year.\n ` +
  "If you think this is a mistake, please reach out to your Hack the North contact.";

const imageForStatus = (
  status: ReturningSponsorInviteStatus
): TAwSnapImage | undefined => {
  switch (status) {
    case ReturningSponsorInviteStatus.Ok:
      return LaptopGearSVG;
    case ReturningSponsorInviteStatus.CompanyNotSponsoring:
      return EmptyFallingStackSVG;
    default:
      return undefined;
  }
};

const titleForStatus = (
  status: ReturningSponsorInviteStatus
): string | undefined => {
  switch (status) {
    case ReturningSponsorInviteStatus.CompanyNotSponsoring:
      return "Thank you for your past support";
    case ReturningSponsorInviteStatus.Ok:
      return "Welcome back!";
    case ReturningSponsorInviteStatus.NotPreviousSponsor:
    case ReturningSponsorInviteStatus.AlreadySponsor:
    default:
      return undefined;
  }
};

const messageForStatus = (
  status: ReturningSponsorInviteStatus,
  companyName?: string | null
): string => {
  switch (status) {
    case ReturningSponsorInviteStatus.CompanyNotSponsoring:
      return NOT_SPONSORING_MESSAGE(companyName);
    case ReturningSponsorInviteStatus.Ok:
      return MISSING_INVITE_MESSAGE;
    case ReturningSponsorInviteStatus.NotPreviousSponsor:
    case ReturningSponsorInviteStatus.AlreadySponsor:
    default:
      return DEFAULT_MESSAGE;
  }
};

const logOutLabel = (email?: string | null) =>
  email ? `Log out of ${email}` : "Log out";

const UnauthorizedError: React.FC = () => {
  const { logOut, info } = useUserContext();

  const { data, loading } = useGetReturningSponsorInviteCodeQuery();

  const result = data?.returningSponsorInvite;
  const inviteCode = result?.invite_code ?? null;

  if (loading) {
    return null;
  }

  if (inviteCode) {
    const companyName = result?.company_name;
    const company = companyName || "your company";
    return (
      <AwSnap
        title="Welcome back!"
        error={new Error(RETURNING_SPONSOR_MESSAGE(companyName))}
        image={LaptopGearSVG}
      >
        <Button
          onClick={() => {
            window.location.href = getSponsorAuthUrl(inviteCode, "login");
          }}
        >
          {`Sign in to ${company}'s dashboard`}
        </Button>
        <Spacer height={16} />
        <Button onClick={logOut} color="secondary">
          {logOutLabel(info?.email)}
        </Button>
      </AwSnap>
    );
  }

  const message = result
    ? messageForStatus(result.status, result.company_name)
    : DEFAULT_MESSAGE;
  const title = result ? titleForStatus(result.status) : undefined;
  const image = result ? imageForStatus(result.status) : undefined;

  return (
    <AwSnap title={title} image={image} error={new Error(message)}>
      <Button onClick={logOut}>{logOutLabel(info?.email)}</Button>
    </AwSnap>
  );
};

export default UnauthorizedError;
